import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Check, X } from "lucide-react";
import {
  addPaymentDetails,
  GetBankDetailsByRoutingNumber,
  getPaymentdetailsByUserid,
  makeDefaultPaymentMethod,
} from "../../reducers/authentication.slice";
import { convertDateFormatInUtc } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { getBankImageURL } from "../../utils/getBankImage";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import PageHeader from "../../components/shared/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

const blockInvalidInt = (e) => {
  const invalidChars = ["e", "E", ".", ",", "+", "-"];
  if (
    invalidChars.includes(e.key) ||
    (!/^\d$/.test(e.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
      !e.ctrlKey &&
      !e.metaKey)
  ) {
    e.preventDefault();
  }
};

const blockNonDigitPaste = (e) => {
  const pasteData = e.clipboardData.getData("text");
  if (!/^\d+$/.test(pasteData)) e.preventDefault();
};

const PaymentInput = ({ style, ...props }) => (
  <Input
    {...props}
    style={{ width: "100%", minWidth: 0, boxSizing: "border-box", ...style }}
  />
);

const PaymentMethodPages = () => {
  const [open, setOpen] = useState(false);
  const [paymentType, setPaymentType] = useState("ACH");
  const [formData, setFormData] = useState({
    account_holder_name: "",
    phone_number: "",
    email: "",
    routing_number: "",
    account_number: "",
    paypal_username: "",
  });
  const [errors, setErrors] = useState({
    account_holder_name: "",
    phone_number: "",
    email: "",
    routing_number: "",
    account_number: "",
    paypal_username: "",
  });
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [bankDetails, setBankDetails] = useState({});

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  const getAllISF = () => {
    setShowLoader(true);
    dispatch(getPaymentdetailsByUserid()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      setPaymentDetails(res?.payload);
    });
  };

  useEffect(() => { getAllISF(); }, []);

  const handlePaymentTypeChange = (value) => {
    setPaymentType(value);
    setFormData({ account_holder_name: "", phone_number: "", email: "", routing_number: "", account_number: "", paypal_username: "" });
    setBankDetails({});
    setErrors({ account_holder_name: "", phone_number: "", email: "", routing_number: "", account_number: "", paypal_username: "" });
  };

  const handlePaymentsChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
    if (value.trim() !== "") {
      if (name === "email" && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
      } else if (name === "phone_number" && !/^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/.test(value)) {
        setErrors((prev) => ({ ...prev, phone_number: "Please enter a valid US phone number" }));
      }
    }
  };

  const isFormValid = () => {
    const isValidEmail = (e) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(e);
    const isValidUSPhoneNumber = (p) => /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/.test(p);
    if (paymentType === "ACH") {
      return (
        formData.account_holder_name?.trim() !== "" &&
        isValidUSPhoneNumber(formData.phone_number) &&
        isValidEmail(formData.email) &&
        formData.routing_number?.trim() !== "" &&
        formData.account_number?.trim() !== ""
      );
    } else if (paymentType === "PayPal") {
      return (
        formData.account_holder_name?.trim() !== "" &&
        isValidEmail(formData.email) &&
        formData.paypal_username?.trim() !== ""
      );
    }
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowLoader(true);
    const payload = {
      payment_method: paymentType,
      ...formData,
      bank_name: bankDetails?.bank_name || "",
      is_primary: paymentDetails?.length < 1 ? true : false,
    };
    if (paymentType === "PayPal") delete payload.bank_name;
    dispatch(addPaymentDetails(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Payment method added successfully", "success");
      setShowLoader(false);
      getAllISF();
      setOpen(false);
    });
  };

  const maskSensitiveNumber = (number) => {
    if (!number) return "****";
    const lastFour = number.slice(-4);
    const maskedPortion = "*".repeat(Math.max(0, number.length - 4));
    return `${maskedPortion}${lastFour}`;
  };

  const handleMakePrimary = (id) => {
    setShowLoader(true);
    dispatch(makeDefaultPaymentMethod({ id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Payment method made primary successfully", "success");
      setShowLoader(false);
      getAllISF();
    });
  };

  const handleGetBankDetails = (routingNumber) => {
    setShowLoader(true);
    dispatch(GetBankDetailsByRoutingNumber({ routing_number: routingNumber })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        setBankDetails({});
        setErrors((prev) => ({ ...prev, routing_number: "We couldn't check this routing number right now." }));
        showToast("We couldn't check this routing number right now. Please double-check it before saving.", "error");
        return;
      }
      if (res?.payload?.length === 0) {
        setShowLoader(false);
        setBankDetails({});
        setErrors((prev) => ({ ...prev, routing_number: "We couldn't find a bank for this routing number." }));
        showToast("We couldn't find a bank for this routing number. Please double-check it before saving.", "warning");
        return;
      }
      setBankDetails(res?.payload[0]);
      setShowLoader(false);
    });
  };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <PageHeader
          title="Saved Payment Methods"
          subtitle="Keep track of all your payment method details here."
        />
        <Button onClick={() => setOpen(true)} style={{ flexShrink: 0, marginTop: 4 }}>
          Add New Payment Method
        </Button>
      </div>

      {/* Payment methods grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 16,
          marginTop: 8,
        }}
      >
        {paymentDetails?.map((item, index) => (
          <div
            key={index}
            style={{
              background: "#101010",
              padding: 24,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <div style={{ flex: 1, display: "flex", gap: 12, alignItems: "flex-start" }}>
                {/* Logo / bank image */}
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 5,
                    background:
                      item.payment_method === "PayPal"
                        ? "linear-gradient(90deg, rgba(1,13,131,1), rgba(0,5,49,1))"
                        : "#ffffff",
                    border: "1px solid rgba(1,13,131,1)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 4,
                    flexShrink: 0,
                  }}
                >
                  {item.payment_method === "PayPal" ? (
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/PayPal_Logo_Icon_2014.svg/1200px-PayPal_Logo_Icon_2014.svg.png"
                      alt="PayPal"
                      style={{ height: 30 }}
                    />
                  ) : item?.bank_name && getBankImageURL(item?.bank_name) ? (
                    <img
                      src={getBankImageURL(item?.bank_name)}
                      alt="Bank"
                      style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <span style={{ fontWeight: 500, color: "#000", fontSize: "0.7rem", textAlign: "center" }}>
                      {item?.bank_name || "Bank"}
                    </span>
                  )}
                </div>
                {/* Details */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: "1.2rem", letterSpacing: 1 }}>
                    {item.payment_method === "ACH" && item?.bank_name
                      ? item.bank_name.split(",")[0]
                      : item.payment_method?.replace("_", " ")}
                  </span>
                  <span style={{ fontSize: 13, color: "#c3c8cc" }}>
                    {item.payment_method === "ACH"
                      ? maskSensitiveNumber(item.account_number)
                      : item.payment_method === "PayPal"
                        ? item.paypal_username || item.email
                        : maskSensitiveNumber(item.account_number)}
                  </span>
                </div>
              </div>
              {/* Primary indicator */}
              <div style={{ flexShrink: 0 }}>
                {item?.is_primary ? (
                  <div
                    style={{
                      width: 35,
                      height: 35,
                      borderRadius: "50%",
                      background: "rgba(0,131,143,0.16)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Check size={16} />
                  </div>
                ) : (
                  <span
                    style={{ color: "#2196F3", cursor: "pointer", fontSize: 13 }}
                    onClick={() => handleMakePrimary(item?.id)}
                  >
                    Make Primary
                  </span>
                )}
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <span style={{ fontSize: 12, color: "#ffffff85" }}>
                Added on {convertDateFormatInUtc(item?.created_at, "MM/DD/YYYY")}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Add Payment Method Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ maxWidth: 500 }}>
          <DialogHeader>
            <DialogTitle>Add New Payment Method</DialogTitle>
            <DialogDescription>
              Select payment method first and enter the corresponding details.
            </DialogDescription>
          </DialogHeader>

          {/* Payment type radio */}
          <div style={{ display: "flex", gap: 24, marginBottom: 16, width: "100%", minWidth: 0 }}>
            {["ACH", "PayPal"].map((type) => (
              <label key={type} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 14 }}>
                <input
                  type="radio"
                  name="paymentType"
                  value={type}
                  checked={paymentType === type}
                  onChange={() => handlePaymentTypeChange(type)}
                  style={{ accentColor: "#00ADB5" }}
                />
                {type === "ACH" ? "Bank Account" : "PayPal"}
              </label>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%", minWidth: 0 }}>
            {paymentType === "ACH" ? (
              <>
                <div>
                  <Label>Full Name <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput type="text" name="account_holder_name" value={formData.account_holder_name} placeholder="Account Holder Name" onChange={handlePaymentsChange} />
                </div>
                <div>
                  <Label>Phone Number <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput
                    type="number"
                    name="phone_number"
                    value={formData.phone_number}
                    placeholder="Phone Number"
                    onChange={handlePaymentsChange}
                    onKeyDown={blockInvalidInt}
                    onPaste={blockNonDigitPaste}
                    onWheel={(e) => e.target.blur()}
                    style={{ appearance: "textfield" }}
                  />
                  {errors.phone_number && <span style={{ color: "#f27059", fontSize: 12 }}>{errors.phone_number}</span>}
                </div>
                <div>
                  <Label>Email Address <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput type="text" name="email" value={formData.email} placeholder="Email Address" onChange={handlePaymentsChange} />
                  {errors.email && <span style={{ color: "#f27059", fontSize: 12 }}>{errors.email}</span>}
                </div>
                <div>
                  <Label>Routing Number <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput
                    type="text"
                    name="routing_number"
                    value={formData.routing_number}
                    placeholder="Routing Number"
                    onChange={handlePaymentsChange}
                    onKeyDown={blockInvalidInt}
                    onPaste={blockNonDigitPaste}
                    onInput={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      e.target.value = value.slice(0, 9);
                    }}
                    onBlur={(e) => handleGetBankDetails(e.target.value)}
                  />
                  {errors.routing_number && <span style={{ color: "#f27059", fontSize: 12 }}>{errors.routing_number}</span>}
                </div>
                <div>
                  <Label>Account Number <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput
                    type="number"
                    name="account_number"
                    value={formData.account_number}
                    placeholder="Account Number"
                    onChange={handlePaymentsChange}
                    onKeyDown={blockInvalidInt}
                    onPaste={blockNonDigitPaste}
                    onWheel={(e) => e.target.blur()}
                    style={{ appearance: "textfield" }}
                  />
                </div>
                {bankDetails?.bank_name && (
                  <div
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      padding: 12,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <p style={{ color: "#00ADB5", fontWeight: 600, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                      Bank Information
                    </p>
                    <p style={{ fontSize: 13 }}>
                      <span style={{ color: "#ffffff85", marginRight: 8 }}>Bank Name:</span>
                      <span>{bankDetails?.bank_name}</span>
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <Label>Full Name <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput type="text" name="account_holder_name" value={formData.account_holder_name} placeholder="Account Holder Name" onChange={handlePaymentsChange} />
                </div>
                <div>
                  <Label>Email Address <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput type="text" name="email" value={formData.email} placeholder="Email Address" onChange={handlePaymentsChange} />
                  {errors.email && <span style={{ color: "#f27059", fontSize: 12 }}>{errors.email}</span>}
                </div>
                <div>
                  <Label>PayPal Username <span style={{ color: "red" }}>*</span></Label>
                  <PaymentInput type="text" name="paypal_username" value={formData.paypal_username} placeholder="PayPal Username" onChange={handlePaymentsChange} />
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!isFormValid()}>Submit</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethodPages;
